import { Box, Button, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import EntityEventSubscriptions from './entityEventSubscriptions';
import { useApiContext } from '@/contexts/apiContext';
import ProgressIndicator from '@/components/progressIndicator';
import ErrorMessage from '@/components/errorMessage';
import EntityRecord from './entityRecord';
import { useElementSize } from '@custom-react-hooks/use-element-size';
import EntitySettings from './entitySettings';
import TabPanel from '@/components/tabPanel';
import { useTranslation } from 'react-i18next';
import SettingsValidator from '../settingsValidators';
import SourceEntities from './sourceEntities';
import { EntityType } from '../entityType';
import AgentExecutionModeSettings from './agentExecutionModeSettings';
import { eventNameEquals } from '@/utils';

type Props = {
  entity: any;
  maxHeight?: number;
  getNewEntities?: () => any[];
  getAllEntities?: () => any[];
  onCancel?: () => void;
  onSubmit: (entity: any) => void;
  isNew?: boolean;
};
const settingsValidator = new SettingsValidator();

const EntityConfiguration = ({
  entity = {},
  maxHeight,
  getNewEntities,
  getAllEntities,
  onCancel,
  onSubmit,
  isNew,
}: Props) => {
  const { t } = useTranslation();
  const apiContext = useApiContext();

  const entitDef = entity.dataSourceDef || entity.agentDef || entity.userAppDef;
  const [eventSources, setEventSources] = useState<any[]>([]);
  const [sourceEntities, setSourceEntities] = useState<any[]>([]);
  const [consumesEvents, setConsumesEvent] = useState<boolean>(entitDef?.consumedEvents?.length > 0);
  const [hasSettings, setHasSettings] = useState<boolean>(entitDef?.settingDefs?.length > 0);
  const [hasSourceAgents, setHasSourceAgents] = useState<boolean>(entitDef?.sourceAgentDefs?.length > 0);
  const [selectedTab, setSelectedTab] = useState(
    entitDef?.settingDefs?.length > 0
      ? 'settings'
      : entitDef?.consumedEvents?.length > 0
        ? 'eventSubscriptions'
        : entitDef?.sourceAgentDefs?.length > 0
          ? 'sourceAgents'
          : '',
  );

  const [entityRecord, setEntityRecord] = useState(entity);
  const [settingDefs, setSettingDefs] = useState<any[]>(entitDef?.settingDefs || []);
  const [hasChanged, setHasChanged] = useState(false);

  const [errors, setErrors] = useState(() => {
    if (isNew) return {};
    let [initialErrors = {}] = settingsValidator.validateSettings(entitDef?.settingDefs || [], entity.settings, t);
    if (!entity.name) {
      initialErrors['name'] = t('common.errors.requiredField');
    }
    return initialErrors;
  });
  const [hasErrors, setHasErrors] = useState(() => {
    if (isNew) return false;
    let [, initialHasError = false] = settingsValidator.validateSettings(
      entitDef?.settingDefs || [],
      entity.settings,
      t,
    );
    return initialHasError || !entity.name;
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>();

  useEffect(() => {
    const getEventSources = async () => {
      try {
        setLoading(true);
        const response = await apiContext.getEventSources(entity.entityType, entity.entityDefId);
        if (getNewEntities) {
          const newEntities = getNewEntities();
          const entitDef = entity.dataSourceDef || entity.agentDef || entity.userAppDef;
          for (const consumedEvent of entitDef.consumedEvents || []) {
            const entities = newEntities.filter(
              (e) =>
                e.entityType === consumedEvent.entityType &&
                (!consumedEvent.entityDefId ||
                  consumedEvent.entityDefId === '*' ||
                  consumedEvent.entityDefId === e.entityDefId),
            );
            response.push(
              ...entities.map((ent) => {
                const entityDef = ent.dataSourceDef || ent.agentDef || ent.userAppDef;
                return {
                  entityId: ent.id,
                  entityName: ent.name,
                  entityType: ent.entityType,
                  entityDefId: entityDef.id,
                  entityDefName: entityDef.name,
                  eventDefs: entityDef.emittedEventDefs || [],
                };
              }),
            );
          }
        }
        setEventSources(response);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    const getSourceEntities = async () => {
      try {
        setLoading(true);
        const response = await apiContext.getEntitySources(entity.entityDefId);
        if (getNewEntities) {
          const newEntities = getNewEntities();
          const entitDef = entity.dataSourceDef || entity.agentDef || entity.userAppDef;
          for (const sourceAgentDefId of entitDef.sourceAgentDefs || []) {
            const entities = newEntities.filter(
              (e) =>
                e.entityType === EntityType.agentInstance &&
                (sourceAgentDefId === e.entityDefId || sourceAgentDefId === '*'),
            );
            response.push(
              ...entities.map((ent) => {
                const entityDef = ent.dataSourceDef || ent.agentDef || ent.userAppDef;
                return {
                  entityType: ent.entityType,
                  entityDefId: entityDef.id,
                  entityDefName: entityDef.name,
                  entityId: ent.id,
                  entityName: ent.name,
                };
              }),
            );
          }
        }
        setSourceEntities(response);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    if (consumesEvents) {
      getEventSources();
    }
    if (hasSourceAgents) {
      getSourceEntities();
    }

    setEntityRecord(entity);
    setSettingDefs(entitDef?.settingDefs || []);
  }, [entity, consumesEvents, hasSourceAgents]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  const onNameChange = (name: string) => {
    setEntityRecord({ ...entityRecord, name });
    setHasChanged(true);
  };

  const onDescriptionChange = (description: string) => {
    setEntityRecord({ ...entityRecord, description });
    setHasChanged(true);
  };

  const onSettingChange = (settingName: string, value: any) => {
    setEntityRecord({ ...entityRecord, settings: { ...entityRecord.settings, [settingName]: value } });
    setHasChanged(true);
  };

  const addSubscription = (eventSubscription: any) => {
    setEntityRecord({
      ...entityRecord,
      eventSubscriptions: [...(entityRecord.eventSubscriptions || []), eventSubscription],
    });
    setHasChanged(true);
  };

  const addSourceEntity = (sourceEntity: any) => {
    setEntityRecord({
      ...entityRecord,
      sourceEntities: [...(entityRecord.sourceEntities || []), sourceEntity],
    });
    setHasChanged(true);
  };

  const editSubscription = (oldSubscription: any, newSubscription: any) => {
    setEntityRecord({
      ...entityRecord,
      eventSubscriptions: entityRecord.eventSubscriptions.map((es: any) => {
        if (
          es.eventSourceId !== oldSubscription.eventSourcId &&
          es.eventSourceType !== oldSubscription.eventSourceType &&
          es.eventSourceDefId !== oldSubscription.eventSourceDefId &&
          !eventNameEquals(es.eventName, oldSubscription.eventName)
        ) {
          return newSubscription;
        } else {
          return es;
        }
      }),
    });
    setHasChanged(true);
  };

  const deleteSubscription = (eventSubscription: any) => {
    setEntityRecord({
      ...entityRecord,
      eventSubscriptions: entityRecord.eventSubscriptions.filter(
        (es: any) =>
          es.eventSourceId !== eventSubscription.eventSourceId ||
          es.eventSourceType !== eventSubscription.eventSourceType ||
          es.eventSourceDefId !== eventSubscription.eventSourceDefId ||
          !eventNameEquals(es.eventName, eventSubscription.eventName),
      ),
    });
    setHasChanged(true);
  };

  const deleteSourceEntity = (sourceEntity: any) => {
    setEntityRecord({
      ...entityRecord,
      sourceEntities: entityRecord.sourceEntities.filter(
        (es: any) =>
          es.sourceEntityId !== sourceEntity.sourceEntityId ||
          es.sourceEntityType !== sourceEntity.sourceEntityType ||
          es.sourceEntityDefId !== sourceEntity.sourceEntityDefId,
      ),
    });
    setHasChanged(true);
  };

  const submit = async () => {
    try {
      let [errors = {}, hasError = false] = settingsValidator.validateSettings(settingDefs, entityRecord.settings, t);

      if (hasError) {
        setSelectedTab('settings');
      }
      if (!entityRecord.name) {
        errors['name'] = t('common.errors.requiredField');
        hasError = true;
      }

      setErrors(errors);
      setHasErrors(hasError);
      if (!hasError) {
        onSubmit(entityRecord);
        setHasChanged(false);
      }
    } catch (error) {
      setError(error);
    }
  };

  return (
    <Box
      sx={{
        height: maxHeight ? `${maxHeight}px` : '100%',
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', p: 1 }}>
        <Box sx={{ flexGrow: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          {error && <ErrorMessage error={error} />}
          <Box>
            <EntityRecord
              entity={entityRecord}
              errors={errors}
              onNameChange={onNameChange}
              onDescriptionChange={onDescriptionChange}
            />
          </Box>
          <Tabs value={selectedTab} onChange={handleTabChange} indicatorColor="secondary" sx={{ minHeight: 36, mt: 1 }}>
            {hasSettings && (
              <Tab
                value={'settings'}
                iconPosition="start"
                sx={{ minHeight: 36, py: 0 }}
                label={
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    Settings
                  </Typography>
                }
              />
            )}
            {consumesEvents && (
              <Tab
                value={'eventSubscriptions'}
                iconPosition="start"
                sx={{ minHeight: 36, py: 0 }}
                label={
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    Event Subscriptions
                  </Typography>
                }
              />
            )}
            {hasSourceAgents && (
              <Tab
                value={'sourceAgents'}
                iconPosition="start"
                sx={{ minHeight: 36, py: 0 }}
                label={
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    Source Agents
                  </Typography>
                }
              />
            )}
            {entity.entityType === EntityType.agentInstance && (
              <Tab
                value={'executionMode'}
                iconPosition="start"
                sx={{ minHeight: 36, py: 0 }}
                label={
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#7c3aed' }}>
                    Execution Mode & Tools
                  </Typography>
                }
              />
            )}
          </Tabs>
          <Box sx={{ flexGrow: 1, overflow: 'auto', minHeight: 0 }}>
            {hasSettings && (
              <TabPanel value="settings" currentTab={selectedTab}>
                <EntitySettings entity={entityRecord} errors={errors} onSettingChange={onSettingChange} />
              </TabPanel>
            )}
            {consumesEvents && (
              <TabPanel value="eventSubscriptions" currentTab={selectedTab}>
                {loading && eventSources.length === 0 ? (
                  <ProgressIndicator />
                ) : (
                  <EntityEventSubscriptions
                    entity={entityRecord}
                    eventSources={eventSources}
                    onAddSubscription={addSubscription}
                    onEditSubscription={editSubscription}
                    onDeleteSubscription={deleteSubscription}
                  />
                )}
              </TabPanel>
            )}
            {hasSourceAgents && (
              <TabPanel value="sourceAgents" currentTab={selectedTab}>
                {loading && sourceEntities.length === 0 ? (
                  <ProgressIndicator />
                ) : (
                  <SourceEntities
                    entity={entityRecord}
                    sourceEntities={sourceEntities}
                    onAddSource={addSourceEntity}
                    onDeleteSource={deleteSourceEntity}
                  />
                )}
              </TabPanel>
            )}
            {entity.entityType === EntityType.agentInstance && (
              <TabPanel value="executionMode" currentTab={selectedTab}>
                <AgentExecutionModeSettings
                  entity={entityRecord}
                  onChange={onSettingChange}
                  availableAgents={
                    getAllEntities
                      ? getAllEntities().filter((e) => e.entityType === EntityType.agentInstance)
                      : getNewEntities
                        ? getNewEntities().filter((e) => e.entityType === EntityType.agentInstance)
                        : undefined
                  }
                />
              </TabPanel>
            )}
          </Box>
          {hasErrors && <ErrorMessage error={{ message: 'Please enter valid values in the fields with error.' }} />}
        </Box>
      </Box>
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          flexShrink: 0,
        }}
      >
        {onCancel && (
          <Button size="small" variant="text" onClick={onCancel} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Close
          </Button>
        )}
        <Button
          size="small"
          color="secondary"
          variant="contained"
          onClick={submit}
          disabled={!hasChanged}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: '8px',
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
            color: 'text.primary',
          }}
        >
          {!onCancel ? 'Submit' : 'Ok'}
        </Button>
      </Box>
    </Box>
  );
};

export default EntityConfiguration;
